import React from 'react'

// The final scene of the page: an ocean floor with a shipwreck, kelp,
// starfish, and two clickable artifacts — the treasure chest (CommonGround
// Labs) and a sunken 1950s radio (DialSweep).

const Kelp = ({x, flip, delay}) => (
  <g transform={`translate(${x} 424)${flip ? ' scale(-1 1)' : ''}`}>
    <g className="sf-kelp" style={{animationDelay: delay}}>
      <path d="M0 0 C12 -36 -12 -72 6 -110 C18 -140 -8 -172 2 -200" stroke="#1d5a41" strokeWidth="10" fill="none" strokeLinecap="round"/>
      <path d="M18 0 C28 -30 8 -60 22 -92 C32 -118 14 -142 22 -164" stroke="#17493a" strokeWidth="8" fill="none" strokeLinecap="round"/>
      <path d="M-16 0 C-8 -26 -26 -52 -12 -84 C-4 -108 -20 -128 -14 -146" stroke="#226649" strokeWidth="7" fill="none" strokeLinecap="round"/>
    </g>
  </g>
)

const Starfish = ({x, y, scale, rotate, fill}) => (
  <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}>
    <path
      d="M0 -26 L6.5 -8.9 L24.7 -8 L10.5 3.4 L15.3 21 L0 11 L-15.3 21 L-10.5 3.4 L-24.7 -8 L-6.5 -8.9 Z"
      fill={fill} stroke={fill} strokeWidth="8" strokeLinejoin="round"/>
    <g fill="rgba(255,235,210,0.35)">
      <circle cx="0" cy="-14" r="1.8"/>
      <circle cx="12" cy="-4" r="1.8"/>
      <circle cx="8" cy="11" r="1.8"/>
      <circle cx="-8" cy="11" r="1.8"/>
      <circle cx="-12" cy="-4" r="1.8"/>
    </g>
  </g>
)

const SeaFloor = () => (
  <div style={{marginTop: '12vh'}}>
    <style>{`
      .sf-link { cursor: pointer; }
      .sf-link:focus { outline: 2px solid rgba(255,217,140,0.9); outline-offset: 6px; }
      .sf-lid {
        transition: transform .5s cubic-bezier(.34,1.56,.64,1);
        transform-box: fill-box;
        transform-origin: 8% 95%;
      }
      .sf-gold { opacity: 0; transition: opacity .4s ease .1s; }
      .sf-spark { transition: opacity .3s ease .35s; }
      .sf-chest-g, .sf-radio-g {
        transition: transform .35s ease;
        transform-box: fill-box;
        transform-origin: 50% 100%;
      }
      .sf-chest-a:hover .sf-lid, .sf-chest-a:focus .sf-lid { transform: rotate(-34deg) translateY(-4px); }
      .sf-chest-a:hover .sf-gold, .sf-chest-a:focus .sf-gold { opacity: 1; }
      .sf-chest-a:hover .sf-chest-g, .sf-chest-a:focus .sf-chest-g { transform: scale(1.03); }
      .sf-dial, .sf-needle { transition: fill .35s ease; }
      .sf-radio-glow { opacity: 0; transition: opacity .4s ease; }
      .sf-radio-a:hover .sf-dial, .sf-radio-a:focus .sf-dial { fill: #ffd98c; }
      .sf-radio-a:hover .sf-needle, .sf-radio-a:focus .sf-needle { fill: #4a2a12; }
      .sf-radio-a:hover .sf-radio-glow, .sf-radio-a:focus .sf-radio-glow { opacity: .55; }
      .sf-radio-a:hover .sf-radio-g, .sf-radio-a:focus .sf-radio-g { transform: scale(1.02); }
      .sf-kelp {
        animation: sf-sway 6s ease-in-out infinite alternate;
        transform-box: fill-box;
        transform-origin: 50% 100%;
      }
      .sf-bubble { animation: sf-rise 6s linear infinite; }
      @keyframes sf-sway { from { transform: rotate(-4deg); } to { transform: rotate(4deg); } }
      @keyframes sf-rise {
        0% { transform: translateY(0); opacity: 0; }
        15% { opacity: .5; }
        100% { transform: translateY(-95px); opacity: 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .sf-kelp, .sf-bubble { animation: none; }
      }
    `}</style>
    <svg viewBox="0 0 1500 430" width="100%" style={{display: 'block'}} role="img"
      aria-label="Ocean floor with a shipwreck, a treasure chest linking to CommonGround Labs, and an old radio linking to DialSweep">
      <defs>
        <filter id="sfBlur" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="12"/>
        </filter>
      </defs>

      {/* back dune */}
      <path d="M0 316 Q200 288 420 310 T900 302 T1500 314 L1500 430 L0 430 Z" fill="#3f3828"/>

      {/* anchor, half-buried */}
      <g transform="translate(84 316) rotate(-14)" stroke="#223642" strokeWidth="7" fill="none" strokeLinecap="round">
        <circle cx="0" cy="-40" r="9"/>
        <line x1="0" y1="-31" x2="0" y2="26"/>
        <line x1="-15" y1="-16" x2="15" y2="-16"/>
        <path d="M-24 4 Q-20 26 0 30 Q20 26 24 4"/>
        <path d="M-24 4 L-32 10 M24 4 L32 10"/>
      </g>

      {/* sunken boat */}
      <g transform="translate(140 196) rotate(-8)">
        <g transform="translate(148 36) rotate(6)">
          <rect x="0" y="-80" width="9" height="84" rx="2" fill="#241a12"/>
          <path d="M0 -80 L9 -71 L9 -80 Z" fill="#180f08"/>
        </g>
        <path d="M0 46 L292 12 L274 88 Q160 112 62 96 Q12 74 0 46 Z" fill="#33261c"/>
        <path d="M0 46 L292 12 L288 26 L8 58 Z" fill="#221812"/>
        <path d="M20 72 Q150 98 258 74 M14 60 Q150 86 266 62" stroke="#221812" strokeWidth="3" fill="none"/>
        <g fill="#101c26" stroke="#4a3828" strokeWidth="2.5">
          <circle cx="60" cy="78" r="7.5"/>
          <circle cx="115" cy="73" r="7.5"/>
          <circle cx="170" cy="67" r="7.5"/>
          <circle cx="225" cy="61" r="7.5"/>
        </g>
        {/* bubbles escaping the hull */}
        <g fill="rgba(190,225,250,0.5)">
          <circle className="sf-bubble" cx="176" cy="58" r="3.5"/>
          <circle className="sf-bubble" cx="190" cy="52" r="5" style={{animationDelay: '2s'}}/>
          <circle className="sf-bubble" cx="166" cy="50" r="2.5" style={{animationDelay: '4s'}}/>
        </g>
      </g>

      {/* kelp behind the front dune */}
      <Kelp x={660} delay="0s"/>
      <Kelp x={1416} flip delay="-3s"/>

      {/* front dune */}
      <path d="M0 360 Q260 336 520 356 T1040 352 T1500 356 L1500 430 L0 430 Z" fill="#55482e"/>

      {/* rocks */}
      <g fill="#1c2b33">
        <ellipse cx="442" cy="410" rx="42" ry="15"/>
        <ellipse cx="475" cy="416" rx="24" ry="10" fill="#243740"/>
        <ellipse cx="762" cy="416" rx="30" ry="11"/>
        <ellipse cx="1262" cy="413" rx="36" ry="13"/>
      </g>

      {/* coral sprig */}
      <g transform="translate(1432 416)" stroke="#7a4038" strokeWidth="6" fill="none" strokeLinecap="round">
        <path d="M0 0 C-2 -14 -8 -22 -14 -30 M0 0 C2 -18 10 -26 12 -38 M0 0 C0 -10 -2 -30 4 -44"/>
      </g>

      <Starfish x={500} y={393} scale={1} rotate={12} fill="#c26a4a"/>
      <Starfish x={1005} y={401} scale={0.7} rotate={-24} fill="#c99a55"/>
      <Starfish x={1360} y={396} scale={0.85} rotate={40} fill="#b05a50"/>

      {/* treasure chest → CommonGround Labs */}
      <a href="https://commonground.world" target="_blank" rel="noopener noreferrer"
        className="sf-link sf-chest-a" aria-label="CommonGround Labs">
        <title>Open the treasure — CommonGround Labs</title>
        {/* outer g positions; inner g carries the hover transform so CSS
            doesn't clobber the SVG transform attribute */}
        <g transform="translate(812 262)">
        <g className="sf-chest-g">
          <ellipse cx="85" cy="136" rx="95" ry="12" fill="rgba(0,0,0,0.3)"/>
          <rect x="6" y="66" width="158" height="70" rx="8" fill="#5a3f28"/>
          <path d="M46 66 v70 M85 66 v70 M124 66 v70" stroke="#46311f" strokeWidth="3"/>
          <g className="sf-gold">
            <path d="M10 66 L160 66 L152 48 L18 48 Z" fill="#241708"/>
            <ellipse cx="85" cy="58" rx="72" ry="26" fill="#ffd76a" opacity="0.55" filter="url(#sfBlur)"/>
            <g stroke="#b8862e" strokeWidth="2">
              <circle cx="40" cy="60" r="8" fill="#f2c14e"/>
              <circle cx="60" cy="54" r="9" fill="#e8b23f"/>
              <circle cx="82" cy="58" r="8" fill="#f2c14e"/>
              <circle cx="104" cy="53" r="9" fill="#e8b23f"/>
              <circle cx="126" cy="59" r="8" fill="#f2c14e"/>
              <circle cx="70" cy="64" r="8" fill="#e0a93c"/>
              <circle cx="94" cy="65" r="8" fill="#f2c14e"/>
              <circle cx="116" cy="66" r="7" fill="#e0a93c"/>
            </g>
            <g className="sf-spark" fill="#fff">
              <path transform="translate(52 38)" d="M0 -7 L1.6 -1.6 L7 0 L1.6 1.6 L0 7 L-1.6 1.6 L-7 0 L-1.6 -1.6 Z"/>
              <path transform="translate(112 34) scale(0.7)" d="M0 -7 L1.6 -1.6 L7 0 L1.6 1.6 L0 7 L-1.6 1.6 L-7 0 L-1.6 -1.6 Z"/>
            </g>
          </g>
          <g className="sf-lid">
            <path d="M6 66 L6 40 Q6 12 34 12 L136 12 Q164 12 164 40 L164 66 Z" fill="#6b4a2e"/>
            <path d="M6 58 L164 58 L164 66 L6 66 Z" fill="#523a24"/>
            <rect x="26" y="14" width="14" height="52" fill="#86713f"/>
            <rect x="130" y="14" width="14" height="52" fill="#86713f"/>
            <rect x="76" y="52" width="20" height="16" rx="3" fill="#d9b34a"/>
          </g>
          <rect x="26" y="66" width="14" height="70" fill="#7c6a3f"/>
          <rect x="130" y="66" width="14" height="70" fill="#7c6a3f"/>
          <g>
            <rect x="76" y="66" width="20" height="20" rx="4" fill="#d9b34a"/>
            <circle cx="86" cy="73" r="3.5" fill="#5a3f28"/>
            <path d="M84 75 L88 75 L86 82 Z" fill="#5a3f28"/>
          </g>
        </g>
        </g>
      </a>

      {/* 1950s radio → DialSweep */}
      <a href="https://dialsweep.com" target="_blank" rel="noopener noreferrer"
        className="sf-link sf-radio-a" aria-label="DialSweep">
        <title>Tune the dial — DialSweep</title>
        <g transform="translate(1090 288) rotate(-6)">
        <g className="sf-radio-g">
          <ellipse cx="85" cy="120" rx="90" ry="11" fill="rgba(0,0,0,0.3)"/>
          <ellipse className="sf-radio-glow" cx="85" cy="44" rx="72" ry="30" fill="#ffd98c" filter="url(#sfBlur)"/>
          <path d="M8 118 L8 46 Q8 12 46 12 L124 12 Q162 12 162 46 L162 118 Z" fill="#5d3a28"/>
          <path d="M14 46 Q14 18 46 18 L124 18" stroke="#7a5138" strokeWidth="4" fill="none" strokeLinecap="round"/>
          <rect className="sf-dial" x="34" y="30" width="102" height="26" rx="13" fill="#241a14"/>
          <g fill="rgba(210,190,150,0.5)">
            <rect x="48" y="38" width="2" height="9"/>
            <rect x="62" y="38" width="2" height="9"/>
            <rect x="76" y="38" width="2" height="9"/>
            <rect x="94" y="38" width="2" height="9"/>
            <rect x="108" y="38" width="2" height="9"/>
            <rect x="122" y="38" width="2" height="9"/>
          </g>
          <rect className="sf-needle" x="86" y="33" width="3" height="20" fill="#c9bfa8"/>
          <g fill="#3f2718">
            <rect x="42" y="64" width="6" height="40" rx="3"/>
            <rect x="54" y="64" width="6" height="40" rx="3"/>
            <rect x="66" y="64" width="6" height="40" rx="3"/>
            <rect x="78" y="64" width="6" height="40" rx="3"/>
            <rect x="90" y="64" width="6" height="40" rx="3"/>
            <rect x="102" y="64" width="6" height="40" rx="3"/>
            <rect x="114" y="64" width="6" height="40" rx="3"/>
            <rect x="126" y="64" width="6" height="40" rx="3"/>
          </g>
          <g fill="#d8cdb6">
            <circle cx="26" cy="98" r="9"/>
            <circle cx="144" cy="98" r="9"/>
          </g>
          <g fill="#5d3a28">
            <rect x="24.5" y="91" width="3" height="8" rx="1.5"/>
            <rect x="142.5" y="91" width="3" height="8" rx="1.5"/>
          </g>
          <g fill="#2e1c12">
            <rect x="24" y="116" width="20" height="7" rx="3"/>
            <rect x="126" y="116" width="20" height="7" rx="3"/>
          </g>
        </g>
        </g>
      </a>

      {/* sand tufts over the buried edges */}
      <path d="M780 430 Q820 402 870 430 Z" fill="#55482e"/>
      <path d="M1070 430 Q1120 404 1180 430 Z" fill="#55482e"/>
      <path d="M120 430 Q180 406 260 430 Z" fill="#55482e"/>
    </svg>
  </div>
)

export default SeaFloor
